<template>
  <!-- Self-contained bell + dropdown. Owns the open/closed state and
       the click-outside handler so the layout just drops `<NotificationBell />`
       into the header chrome and stops worrying about coordination. -->
  <div ref="root" class="relative">
    <button
      v-if="authStore.isLoggedIn"
      @click="toggle"
      class="relative p-2 rounded-lg transition-all duration-200 hover:bg-[var(--chrome-pill-hover)]"
      :style="{ color: 'var(--text-secondary)' }"
      :title="unreadCount ? `${unreadCount} unread notification${unreadCount === 1 ? '' : 's'}` : 'Notifications'"
    >
      <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
      <!-- Dynamic badge — count ≥ 1 → numeric pill (capped at 99+ so a
           runaway alert source doesn't blow out the chrome layout). -->
      <span
        v-if="unreadCount > 0"
        :class="badgeClass"
        class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center text-white shadow ring-2 ring-[var(--app-bar-bg,white)] dark:ring-slate-900"
      >
        {{ unreadCount > 99 ? '99+' : unreadCount }}
      </span>
    </button>

    <!-- Dropdown panel. Right-aligned with the bell so the long-form
         body text doesn't run off the screen on narrow windows. -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 scale-95 translate-y-1"
      enter-to-class="opacity-100 scale-100 translate-y-0"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100 scale-100 translate-y-0"
      leave-to-class="opacity-0 scale-95 translate-y-1"
    >
      <div
        v-if="open"
        class="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] origin-top-right rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 shadow-2xl z-50 overflow-hidden"
      >
        <!-- Header. Mark-all-read on the right; only when there's
             something unread to clear. -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/10">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-gray-900 dark:text-white">Notifications</h3>
            <span v-if="unreadCount" class="text-xs text-gray-500">{{ unreadCount }} unread</span>
          </div>
          <button
            v-if="unreadCount"
            @click="onMarkAllRead"
            class="text-xs font-medium text-purple-600 dark:text-purple-300 hover:underline"
          >
            Mark all read
          </button>
        </div>

        <!-- Empty / loading / list states. PulsarLoader sm fits the
             dropdown's tight ~96px content area without dominating it. -->
        <div v-if="!hydrated" class="px-4 py-6">
          <PulsarLoader size="sm" label="" />
        </div>
        <div v-else-if="!recent.length" class="px-4 py-10 text-center">
          <svg class="w-10 h-10 mx-auto text-gray-300 dark:text-white/20 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-sm text-gray-500 dark:text-gray-400">You're all caught up.</p>
        </div>
        <div v-else class="max-h-96 overflow-y-auto">
          <button
            v-for="n in recent"
            :key="n.id"
            @click="onActivate(n)"
            :class="[
              'w-full text-left px-4 py-3 border-b border-gray-100 dark:border-white/5 last:border-b-0',
              'hover:bg-gray-50 dark:hover:bg-white/5 transition-colors',
              isUnread(n) ? 'bg-purple-50/40 dark:bg-purple-500/5' : '',
            ]"
          >
            <div class="flex items-start gap-2">
              <!-- Severity dot — purple for unread+info, red/yellow/blue
                   stay severity-coded so the user can scan urgency
                   without reading the badge. -->
              <span
                :class="['w-2 h-2 rounded-full mt-1.5 shrink-0', dotClass(n)]"
              ></span>
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ n.title }}</p>
                  <span :class="badgeForSeverity(n.severity)" class="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide shrink-0">
                    {{ n.severity }}
                  </span>
                </div>
                <p class="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-2">{{ n.body }}</p>
                <p class="text-[10px] text-gray-400 mt-1">{{ formatRelative(n.createdAt) }}</p>
              </div>
              <button
                @click.stop="onDismiss(n)"
                class="opacity-0 group-hover:opacity-100 p-1 -m-1 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 shrink-0"
                title="Dismiss"
              >
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
          </button>
        </div>

        <!-- Footer link to the full page. Hidden when the user has no
             notifications at all — there's nothing to "view all" of. -->
        <NuxtLink
          v-if="recent.length"
          to="/notifications"
          class="block px-4 py-2.5 text-center text-xs font-medium text-purple-600 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-white/5 border-t border-gray-200 dark:border-white/10"
          @click="open = false"
        >
          View all notifications →
        </NuxtLink>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { AppNotification, NotificationSeverity } from '~/stores/notificationsStore'
import PulsarLoader from '~/components/common/PulsarLoader.vue'

const authStore = useAuthStore()
const router = useRouter()
const {
  visible,
  unread,
  unreadCount,
  hydrated,
  markRead,
  markAllRead,
  dismiss,
} = useNotifications()

const open = ref(false)
const root = ref<HTMLElement | null>(null)

// Cap the dropdown to the most recent 8 items — anything more is a sign
// the user wants the full /notifications page. The bell is for "what
// happened recently" not "what's the complete history."
const recent = computed(() => visible.value.slice(0, 8))

function toggle() { open.value = !open.value }

function isUnread(n: AppNotification): boolean {
  return unread.value.includes(n)
}

// Click-outside-to-close. Bound on first open, removed on close, so
// idle bells don't add a global listener.
function onDocClick(e: MouseEvent) {
  if (!open.value || !root.value) return
  if (!root.value.contains(e.target as Node)) open.value = false
}
watch(open, (isOpen) => {
  if (isOpen) document.addEventListener('mousedown', onDocClick)
  else document.removeEventListener('mousedown', onDocClick)
})
onBeforeUnmount(() => document.removeEventListener('mousedown', onDocClick))

async function onActivate(n: AppNotification) {
  // Mark read first — the navigation re-renders the bell badge from
  // store state, so we want the click to feel immediate.
  await markRead(n.id)
  if (n.link?.path) {
    open.value = false
    router.push({ path: n.link.path, query: n.link.query || {} })
  }
}

async function onDismiss(n: AppNotification) {
  await dismiss(n.id)
}

async function onMarkAllRead() {
  await markAllRead()
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

const badgeClass = computed(() => {
  // Red badge if any unread are critical — otherwise neutral indigo.
  // Both are high-contrast against the chrome regardless of theme.
  return unread.value.some(n => n.severity === 'critical')
    ? 'bg-red-600'
    : 'bg-indigo-600'
})

function dotClass(n: AppNotification): string {
  if (!isUnread(n)) return 'bg-gray-300 dark:bg-white/20'
  return ({
    critical: 'bg-red-500',
    warning: 'bg-yellow-500',
    info: 'bg-blue-500',
  })[n.severity]
}

function badgeForSeverity(s: NotificationSeverity): string {
  return ({
    critical: 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300',
    warning: 'bg-yellow-100 dark:bg-yellow-500/20 text-yellow-700 dark:text-yellow-300',
    info: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
  })[s]
}

// "X minutes ago" — small enough that hand-rolling beats pulling in
// dayjs. Falls back to "just now" if Firestore hasn't filled the
// server timestamp yet (rare; usually a 100ms window).
function formatRelative(ts: { toDate?: () => Date } | null): string {
  if (!ts || typeof ts.toDate !== 'function') return 'just now'
  const ms = Date.now() - ts.toDate().getTime()
  if (ms < 60_000) return 'just now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d ago`
  return formatInUserTz(ts.toDate(), { dateOnly: true })
}

const { formatInUserTz } = useUserTimezone()
</script>
